import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { reviews } from '../../../mocks/reviews';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const review = reviews.find((r) => r.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!review) return;

    document.title = `${review.name} Review – Is It Worth It? | Best Christmas Gift Reviews`;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `Read our expert review of the ${review.name}. ${review.bestFor}. Honest pros, cons, and verdict to help you decide.`);

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Review',
          '@id': `${SITE_URL}/reviews/${review.id}#review`,
          name: `${review.name} Review`,
          url: `${SITE_URL}/reviews/${review.id}`,
          reviewBody: review.verdict,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rating,
            bestRating: 5,
            worstRating: 1,
          },
          author: {
            '@type': 'Organization',
            name: 'Best Christmas Gift Reviews',
            url: SITE_URL,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Best Christmas Gift Reviews',
            url: SITE_URL,
          },
          itemReviewed: {
            '@type': 'Product',
            name: review.name,
            image: review.heroImage || review.image,
            description: review.description,
            category: review.category,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: review.rating,
              bestRating: 5,
              worstRating: 1,
              reviewCount: 1,
            },
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Reviews', item: `${SITE_URL}/reviews` },
            { '@type': 'ListItem', position: 3, name: review.name, item: `${SITE_URL}/reviews/${review.id}` },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-review-detail';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('jsonld-review-detail');
      if (el) el.remove();
    };
  }, [id, review]);

  if (!review) {
    return (
      <div className="min-h-screen bg-[#FFF9F5]">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <div className="max-w-2xl mx-auto px-6">
            <i className="ri-error-warning-line text-6xl text-[#8B2635] mb-6 block"></i>
            <h1 className="text-3xl font-bold text-[#1B4332] mb-4">Review Not Found</h1>
            <p className="text-gray-600 mb-8">Sorry, we couldn't find the review you're looking for.</p>
            <Link 
              to="/reviews" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B4332] text-white rounded-full font-semibold hover:bg-[#2D5A4A] transition-all"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Reviews
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedReviews = reviews
    .filter(r => r.category === review.category && r.id !== review.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1B4332] to-[#2D5A4A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 text-white text-8xl">⭐</div>
          <div className="absolute bottom-10 left-10 text-white text-6xl">🎁</div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <Link 
            to="/reviews" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors group"
          >
            <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
            Back to All Reviews
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <span className="px-4 py-1.5 bg-[#D4AF37] text-[#1B4332] rounded-full text-sm font-bold">
              {review.category}
            </span>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <i key={i} className={`ri-star-fill ${i < review.rating ? 'text-[#D4AF37]' : 'text-white/30'}`}></i>
              ))}
              <span className="ml-2 text-white/80 text-sm">({review.rating}.0 Rating)</span>
            </div>
          </div>
          
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">
            {review.name}
          </h1>
          <p className="text-xl text-white/80 max-w-3xl">
            {review.bestFor}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Product Image */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-xl">
                <div className="relative w-full h-80 lg:h-96 bg-gray-50 flex items-center justify-center p-8">
                  <img 
                    src={review.heroImage} 
                    alt={review.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Overview */}
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-[#1B4332] mb-6 flex items-center gap-3">
                  <i className="ri-file-text-line text-[#D4AF37]"></i>
                  Overview
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {review.description}
                </p>
              </div>

              {/* Key Features */}
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-[#1B4332] mb-6 flex items-center gap-3">
                  <i className="ri-list-check-2 text-[#D4AF37]"></i>
                  Key Features
                </h2>
                <ul className="grid sm:grid-cols-2 gap-4">
                  {review.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[#1B4332] mt-1 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pros & Cons */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-white rounded-3xl p-8 shadow-lg border-t-4 border-green-500">
                  <h3 className="text-xl font-bold text-[#1B4332] mb-6 flex items-center gap-3">
                    <i className="ri-thumb-up-fill text-green-500"></i>
                    What We Love
                  </h3>
                  <ul className="space-y-4">
                    {review.pros.map((pro, index) => (
                      <li key={index} className="flex items-start gap-3 text-gray-700">
                        <i className="ri-check-line text-green-500 mt-1 flex-shrink-0"></i>
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-white rounded-3xl p-8 shadow-lg border-t-4 border-amber-500">
                  <h3 className="text-xl font-bold text-[#1B4332] mb-6 flex items-center gap-3">
                    <i className="ri-thumb-down-fill text-amber-500"></i>
                    Things to Consider
                  </h3>
                  <ul className="space-y-4">
                    {review.cons.map((con, index) => (
                      <li key={index} className="flex items-start gap-3 text-gray-700">
                        <i className="ri-close-line text-amber-500 mt-1 flex-shrink-0"></i>
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Our Verdict */}
              <div className="bg-gradient-to-br from-[#1B4332] to-[#2D5A4A] rounded-3xl p-8 shadow-lg text-white">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <i className="ri-award-fill text-[#D4AF37]"></i>
                  Our Verdict
                </h2>
                <p className="text-white/90 leading-relaxed text-lg mb-8">
                  {review.verdict}
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-fill text-2xl ${i < review.rating ? 'text-[#D4AF37]' : 'text-white/30'}`}></i>
                    ))}
                  </div>
                  <span className="text-[#D4AF37] font-bold text-xl">{review.rating}/5</span>
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                {/* Buy Box */}
                <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-[#D4AF37]">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden">
                      <img 
                        src={review.image} 
                        alt={review.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-[#1B4332] mb-2">{review.name}</h3>
                    <div className="flex items-center justify-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className={`ri-star-fill ${i < review.rating ? 'text-[#D4AF37]' : 'text-gray-300'}`}></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500">Check Amazon for current price</p>
                  </div>
                  
                  <a
                    href={review.amazonLink}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#8B2635] to-[#A52A3A] text-white rounded-full font-bold hover:shadow-lg hover:shadow-[#8B2635]/30 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <i className="ri-amazon-fill text-xl"></i>
                    View on Amazon
                    <i className="ri-external-link-line"></i>
                  </a>
                  
                  <p className="text-xs text-gray-500 text-center mt-4">
                    <i className="ri-information-line"></i> As an Amazon Associate, we earn from qualifying purchases.
                  </p>
                </div>

                {/* Quick Facts */}
                <div className="bg-white rounded-3xl p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-[#1B4332] mb-4">Quick Facts</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">Category</span>
                      <span className="font-semibold text-[#1B4332]">{review.category}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">Our Rating</span>
                      <span className="font-semibold text-[#D4AF37]">{review.rating}/5 Stars</span>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <span className="text-gray-600">Best For</span>
                      <span className="font-semibold text-[#1B4332] text-right text-sm max-w-32">{review.bestFor}</span>
                    </div>
                  </div>
                </div>

                {/* Share */}
                <div className="bg-white rounded-3xl p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-[#1B4332] mb-4">Share This Review</h3>
                  <div className="flex items-center gap-3">
                    {[
                      { icon: 'ri-facebook-fill', color: 'bg-blue-600' },
                      { icon: 'ri-twitter-x-fill', color: 'bg-black' },
                      { icon: 'ri-pinterest-fill', color: 'bg-red-600' },
                      { icon: 'ri-mail-fill', color: 'bg-[#1B4332]' }
                    ].map((social, index) => (
                      <button 
                        key={index}
                        className={`w-10 h-10 ${social.color} rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer`}
                      >
                        <i className={social.icon}></i>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Reviews */}
      {relatedReviews.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#1B4332] mb-8">
              More {review.category} Reviews
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedReviews.map((related) => (
                <Link
                  key={related.id}
                  to={`/reviews/${related.id}`}
                  className="bg-[#FFF9F5] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <div className="relative w-full h-48">
                    <img 
                      src={related.image} 
                      alt={related.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-[#1B4332] rounded-full">
                      <span className="text-white text-xs font-semibold">{related.category}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#1B4332] mb-2 group-hover:text-[#8B2635] transition-colors">
                      {related.name}
                    </h3>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className={`ri-star-fill text-sm ${i < related.rating ? 'text-[#D4AF37]' : 'text-gray-300'}`}></i>
                      ))}
                    </div>
                    <span className="inline-flex items-center gap-2 text-[#8B2635] font-semibold text-sm group-hover:gap-3 transition-all">
                      Read Full Review
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
